import { useId, useState } from 'react'
import { PhoneInput } from '#app/components/ui/phone-input'
import { cn } from '#app/utils/misc'
import { AUTH_FIELD_GAP, type AuthInputProps } from './AuthField'

/**
 * The phone pill. Same look as AuthField, same submission as the old
 * FloatingPhoneInputField: the visible input has no name; a hidden input
 * carries conform's `name` (and `form`) with the E.164 value the library
 * produces ("+14165550123"), which the signup actions parse with
 * libphonenumber. Built on the app's PhoneInput (react-phone-number-input
 * with the searchable country list), restyled through classes only.
 *
 * The label floats and the country button fades in on focus, while the
 * country list is open, or once a number is present (auth.css, `:has()`).
 * In the sign-up popup the button always shows and the label sits after it,
 * so the wrapper carries the button's width as --tt-auth-cc-w.
 */
/**
 * Keeps --tt-auth-cc-w on the wrapper equal to the country button's width
 * (it changes with the dial code: +1, +44, +971).
 */
function trackCountryWidth(wrapper: HTMLDivElement | null) {
	const button = wrapper?.querySelector<HTMLElement>(
		'button[data-slot="button"]',
	)
	if (!wrapper || !button || typeof ResizeObserver === 'undefined') return
	const observer = new ResizeObserver(() => {
		wrapper.style.setProperty('--tt-auth-cc-w', `${button.offsetWidth}px`)
	})
	observer.observe(button)
	return () => observer.disconnect()
}

export function AuthPhoneField({
	label,
	inputProps,
	errors,
	defaultCountry = 'CA',
	hint,
	className,
}: {
	label: string
	/** Shown faintly while focused and empty (e.g. "416-555-0182"). */
	hint?: string
	inputProps: AuthInputProps
	errors?: Array<string | null | undefined> | null
	defaultCountry?: 'CA' | 'US'
	className?: string
}) {
	const fallbackId = useId()
	const {
		key: _key,
		id: givenId,
		name,
		form,
		defaultValue,
		required,
		autoFocus,
		disabled,
	} = inputProps
	const id = givenId ?? fallbackId
	const [value, setValue] = useState(
		typeof defaultValue === 'string' ? defaultValue : '',
	)
	const messages = (errors ?? []).filter(
		(error): error is string => typeof error === 'string' && error !== '',
	)
	const hasError = messages.length > 0
	const errorId = `${id}-error`

	return (
		<div className={cn(AUTH_FIELD_GAP, className)}>
			<input type="hidden" name={name} form={form} value={value} />
			<div className="tt-auth-phone relative" ref={trackCountryWidth}>
				<PhoneInput
					id={id}
					international={false}
					defaultCountry={defaultCountry}
					value={value}
					onChange={(next) => setValue(next || '')}
					placeholder={hint ?? ' '}
					required={required}
					autoFocus={autoFocus}
					disabled={disabled}
					autoComplete="tel"
					aria-invalid={hasError ? true : undefined}
					aria-describedby={hasError ? errorId : undefined}
					className={cn(
						'tt-auth-phone-box h-[50px] w-full items-stretch rounded-full border bg-white transition-[border-color] duration-200 focus-within:border-tt-ink min-[601px]:h-[clamp(46px,6.2vh,56px)]',
						hasError ? 'border-tt-danger' : 'border-tt-line',
						// The country button: flat, inside the pill, no border of its own.
						// Its keyboard focus ring lives in auth.css: the global
						// `.PhoneInput button` rule strips box-shadow outside any layer.
						'[&_button]:h-full [&_button]:gap-1.5 [&_button]:rounded-l-full [&_button]:rounded-r-none [&_button]:border-0 [&_button]:bg-transparent [&_button]:pl-[18px] [&_button]:pr-2 [&_button]:shadow-none [&_button]:hover:bg-transparent [&_button]:focus-visible:ring-0 min-[601px]:[&_button]:pl-[22px]',
						'[&_button_span]:text-[15px] [&_button_span]:font-medium [&_button_span]:text-tt-ink',
						// The number input: the pill draws the border, so none here.
						'[&>div]:min-w-0 [&>div]:flex-1 [&_input]:h-full [&_input]:rounded-r-full [&_input]:border-0! [&_input]:bg-transparent [&_input]:px-1 [&_input]:text-[16px] [&_input]:text-tt-ink [&_input]:focus-visible:ring-0 min-[601px]:[&_input]:text-[15px]',
						'[&_[data-slot=input]]:h-full [&>div]:h-full',
					)}
				/>
				<label htmlFor={id} className="tt-auth-label">
					{label}
				</label>
			</div>
			{hasError ? (
				<p
					id={errorId}
					className="mt-1.5 pl-[18px] text-[14px] font-medium text-tt-danger min-[601px]:pl-[22px]"
				>
					{messages.join(' ')}
				</p>
			) : null}
		</div>
	)
}
